import { serverTimestamp, Timestamp } from 'firebase/firestore';
import { Box, Button } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import dayjs from 'dayjs';
import { ControlledTextInput } from 'src/shared/components/controlledTextInput';
import ControlledDatePicker from 'src/shared/components/controlledDatePicker';
import { Colors } from 'src/shared/styles/colors';
import ControlledSelectGrouped from 'src/shared/components/controlledSelectGrouped';
import ConsoleHelper from 'src/shared/helpers/consoleHelper';
import { DisposeSchema } from 'src/shared/models/disposeSchema';
import { Dispose } from 'src/services/dispose.service';
import { useEffect, useState } from 'react';
import { Category, getCategories } from 'src/services/category.service';
import { appConfig } from 'src/firebaseConf';

type DisposeFormProps = {
  setDisposeData: (arg: Dispose) => void;
  setImageData: (arg: string) => void;
  showForm: boolean;
};

const DisposeForm = (props: DisposeFormProps) => {
  const { setDisposeData, setImageData, showForm } = props;
  const { t } = useTranslation();
  const [categories, setCategories] = useState<Category[]>([]);
  const [imageDataUrl, setImageDataUrl] = useState<string | null>(null);

  useEffect(() => {
    const fetchCategories = async () => {
      const res = await getCategories(undefined, [4]);
      setCategories(res);
    };
    fetchCategories();
  }, []);

  const formMethods = useForm({
    resolver: yupResolver(DisposeSchema()),
    defaultValues: {
      date: dayjs().toDate(),
      category: '',
      title: '',
      description: '',
      quantity: '',
      createdBy: appConfig.companyId,
      //   created: serverTimestamp() as Timestamp,
    },
  });

  const { handleSubmit, watch } = formMethods;

  // TODO: Fix any
  const onPressSubmit = async (x: any) => {
    ConsoleHelper.log('x', x);
    const submitdata: Dispose = {
      companyId: appConfig.companyId,
      date: new Date(x.date),
      category: x.category,
      categoryParent: x.category.categoryParentName,
      title: x.title,
      description: x.description,
      quantity: x.quantity,
      createdBy: appConfig.companyId,
      created: serverTimestamp() as Timestamp,
    };
    ConsoleHelper.log('submitdata', submitdata);
    setDisposeData(submitdata); // Sätter datat i state på mammasidan
    if (imageDataUrl) setImageData(imageDataUrl); // Sätter state för bildsträng på mammasidan
  };

  const categoryValue = watch('category');
  const parsedCategory = categoryValue ? JSON.parse(categoryValue) : undefined;

  return (
    <Box
      id='form-container'
      mt={2}
      sx={{
        display: showForm ? 'flex' : 'none',
        flex: 1,
        flexDirection: 'column',
      }}
    >
      <FormProvider {...formMethods}>
        <form
          id='form'
          onSubmit={handleSubmit(onPressSubmit)}
          style={{ display: 'flex', flex: 1, flexDirection: 'column' }}
        >
          <ControlledDatePicker name='date' label={t('date')} />
          <ControlledSelectGrouped name='category' label={t('category')} options={categories} />
          <ControlledTextInput name='title' label={t('productTitle')} />
          {process.env.REACT_APP_USE_DESCRIPTION === 'true' && (
            <ControlledTextInput name='description' label={t('description')} />
          )}
          <ControlledTextInput
            name='quantity'
            label={parsedCategory && parsedCategory.unit ? `${t('quantity')} (${parsedCategory.unit})` : t('quantity')}
            type='number'
          />
          {process.env.REACT_APP_USE_IMAGES === 'true' && (
            <Box py={2}>
              <input
                type='file'
                accept='image/*'
                onChange={async (event) => {
                  if (event.target.files && event.target.files[0]) {
                    const file = event.target.files[0];
                    const reader = new FileReader();

                    reader.onloadend = () => {
                      setImageDataUrl(reader.result as string);
                    };
                    reader.readAsDataURL(file);
                  }
                }}
              />
            </Box>
          )}
          <Box
            id='submit-container'
            sx={{
              display: 'flex',
              flex: 1,
              alignItems: 'flex-end',
              width: '100%',
            }}
          >
            <Button
              type='submit'
              variant='contained'
              fullWidth
              sx={{
                height: '50px',
                '&.Mui-disabled': {
                  background: Colors.GREEN_OPACITY,
                },
              }}
            >
              {t('next')}
            </Button>
          </Box>
        </form>
      </FormProvider>
    </Box>
  );
};

export default DisposeForm;
